// src/components/settings/shared/SimpleRecycleBinTable.tsx
import React from 'react';
import { Alert, Table, TableRow, Typography } from '@mui/material';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableActionsCell,
} from './SettingsTableElements';
import RecycleBinActions from './RecycleBinActions';

export interface SimpleDeletedItem {
  id: number;
  name: string;
  subLabel?: string;
  deletedAt: string;
}

interface SimpleRecycleBinTableProps {
  items: SimpleDeletedItem[];
  totalCount: number;
  searchText: string;
  entityLabelSingular: string;
  entityLabelPlural: string;
  columnLabel?: string;
  subColumnLabel?: string;
  onRestore: (item: SimpleDeletedItem) => void;
  onDelete: (item: SimpleDeletedItem) => void;
  actionInProgress: string | null;
  busy: boolean;
}

const formatDeletedDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Shared table body for the simpler Recycle Bin entity types (Vets, Pet
// Types/Sizes/Breeds/Colours, Kennel Sizes/Sides) - these are all just a
// name plus an optional parent label, unlike Clients/Pets/Bookings/Users
// which have their own richer, bespoke layouts. Keeps RecycleBin.tsx from
// repeating near-identical table markup six times over.
const SimpleRecycleBinTable: React.FC<SimpleRecycleBinTableProps> = ({
  items,
  totalCount,
  searchText,
  entityLabelSingular,
  entityLabelPlural,
  columnLabel = 'Name',
  subColumnLabel,
  onRestore,
  onDelete,
  actionInProgress,
  busy,
}) => {
  if (totalCount === 0) {
    return <Alert severity="info">No deleted {entityLabelPlural}.</Alert>;
  }

  if (items.length === 0) {
    return (
      <Alert severity="info">
        No deleted {entityLabelPlural} match "{searchText}".
      </Alert>
    );
  }

  return (
    <StyledTableContainer>
      <Table size="small">
        <StyledTableHead>
          <TableRow>
            <StyledTableHeaderCell>{columnLabel}</StyledTableHeaderCell>
            {subColumnLabel && (
              <StyledTableHeaderCell>{subColumnLabel}</StyledTableHeaderCell>
            )}
            <StyledTableHeaderCell>Deleted</StyledTableHeaderCell>
            <StyledTableHeaderCell align="right">Actions</StyledTableHeaderCell>
          </TableRow>
        </StyledTableHead>
        <StyledTableBody>
          {items.map((item, index) => (
            <StyledTableRow key={item.id} isOdd={index % 2 === 0}>
              <StyledTableCell>
                <Typography>{item.name}</Typography>
              </StyledTableCell>
              {subColumnLabel && (
                <StyledTableCell>
                  <Typography>{item.subLabel ?? '-'}</Typography>
                </StyledTableCell>
              )}
              <StyledTableCell>
                <Typography>{formatDeletedDate(item.deletedAt)}</Typography>
              </StyledTableCell>
              <StyledTableActionsCell align="right">
                <RecycleBinActions
                  itemId={item.id}
                  itemName={item.name}
                  onRestore={() => onRestore(item)}
                  onDelete={() => onDelete(item)}
                  actionInProgress={actionInProgress}
                  busy={busy}
                  restoreTooltip={`Restore ${entityLabelSingular}`}
                  deleteTooltip="Permanently delete"
                />
              </StyledTableActionsCell>
            </StyledTableRow>
          ))}
        </StyledTableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default SimpleRecycleBinTable;
